<template>
    <b-modal :visible="isActive" :title="$t('dmp.deviceSetup.cardFormat')" size="xl" modal-class="card-format-modal"
        @hidden="$emit('update:is-active', false)" hide-footer>
        <validation-observer ref="formObserver">
            <b-form>
                <b-row class="p-2">
                    <!-- Left: Table -->
                    <b-col cols="4">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <h6 class="mb-0">{{ $t('dmp.deviceSetup.cardFormats') }}</h6>
                            <b-button variant="primary" size="sm" @click="addNewCardFormat">
                                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                                {{ $t('actions.add') }}
                            </b-button>
                        </div>

                        <b-table :items="validCardFormats" :fields="tableFields" responsive striped hover show-empty
                            selectable select-mode="single" @row-selected="onRowSelected"
                            :empty-text="$t('dmp.deviceSetup.noCardFormats')" class="mb-0">
                            <template #cell(formatNumber)="data">
                                {{ data.item.formatNumber }}
                            </template>
                            <template #cell(formatName)="data">
                                {{ data.item.formatName || '-' }}
                            </template>
                        </b-table>
                    </b-col>

                    <!-- Right: Form -->
                    <b-col cols="8">
                        <b-card v-if="selectedFormat" no-body class="border">
                            <b-card-header class="pt-1 pb-1">
                                <h6 class="mb-0">{{ isEditing ? $t('dmp.deviceSetup.editCardFormat') :
                                    $t('dmp.deviceSetup.newCardFormat')
                                    }}</h6>
                            </b-card-header>
                            <b-card-body>
                                <b-row>
                                    <!-- Format Number -->
                                    <b-col cols="6">
                                        <validation-provider #default="{ errors }" name="Format Number"
                                            rules="required">
                                            <b-form-group label="Format Number" label-for="formatNumber">
                                                <b-form-input id="formatNumber" v-model="form.formatNumber"
                                                    type="number" min="1" max="8" :disabled="isEditing"
                                                    :state="errors.length > 0 ? false : null" />
                                                <small class="text-danger">{{ errors[0] }}</small>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <!-- Format Name -->
                                    <b-col cols="6">
                                        <validation-provider #default="{ errors }" name="Format Name"
                                            rules="required|max:16">
                                            <b-form-group label="Format Name" label-for="formatName">
                                                <b-form-input id="formatName" v-model="form.formatName" type="text"
                                                    maxlength="16" :state="errors.length > 0 ? false : null" />
                                                <small class="text-danger">{{ errors[0] }}</small>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <!-- Wiegand Code Length -->
                                    <b-col cols="6">
                                    <validation-provider #default="{ errors }" name="Wiegand Code Length" rules="required|min_value:1|max_value:255">
                                        <b-form-group label="Wiegand Code Length" label-for="wiegandCodeLength">
                                        <b-form-input id="wiegandCodeLength" v-model="form.wiegandCodeLength"
                                            type="number" min="1" max="255"
                                            :state="errors.length > 0 ? false : null" />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                        </b-form-group>
                                    </validation-provider>
                                    </b-col>

                                    <!-- Site Code Position -->
                                    <b-col cols="6">
                                    <validation-provider #default="{ errors }" name="Site Code Position" rules="required|min_value:0|max_value:255">
                                        <b-form-group label="Site Code Position" label-for="siteCodePosition">
                                        <b-form-input id="siteCodePosition" v-model="form.siteCodePosition"
                                            type="number" min="0" max="255"
                                            :state="errors.length > 0 ? false : null" />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                        </b-form-group>
                                    </validation-provider>
                                    </b-col>

                                    <!-- Site Code Length -->
                                    <b-col cols="6">
                                    <validation-provider #default="{ errors }" name="Site Code Length" rules="required|min_value:1|max_value:24">
                                        <b-form-group label="Site Code Length" label-for="siteCodeLength">
                                        <b-form-input id="siteCodeLength" v-model="form.siteCodeLength"
                                            type="number" min="1" max="24"
                                            :state="errors.length > 0 ? false : null" />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                        </b-form-group>
                                    </validation-provider>
                                    </b-col>

                                    <!-- User Code Position -->
                                    <b-col cols="6">
                                    <validation-provider #default="{ errors }" name="User Code Position" rules="required|min_value:0|max_value:255">
                                        <b-form-group label="User Code Position" label-for="userCodePosition">
                                        <b-form-input id="userCodePosition" v-model="form.userCodePosition"
                                            type="number" min="0" max="255"
                                            :state="errors.length > 0 ? false : null" />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                        </b-form-group>
                                    </validation-provider>
                                    </b-col>

                                    <!-- User Code Length -->
                                    <b-col cols="6">
                                    <validation-provider #default="{ errors }" name="User Code Length" rules="required|min_value:16|max_value:64">
                                        <b-form-group label="User Code Length" label-for="userCodeLength">
                                        <b-form-input id="userCodeLength" v-model="form.userCodeLength"
                                            type="number" min="16" max="64"
                                            :state="errors.length > 0 ? false : null" />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                        </b-form-group>
                                    </validation-provider>
                                    </b-col>


                                    <!-- Site Codes -->
                                    <b-col cols="12">
                                        <h6 class="mt-1">Site Codes</h6>
                                    </b-col>
                                    
                                    <!-- Require Site Code -->
                                    <b-col cols="12" class="d-flex align-items-center">
                                        <b-form-checkbox v-model="form.requireSiteCode" class="my-2">
                                            Require Site Code
                                        </b-form-checkbox>
                                    </b-col>

                                    <b-col cols="3" v-for="n in 8" :key="n">
                                        <b-form-group :label="`Site Code ${n}`">
                                            <b-form-input :disabled="!form.requireSiteCode" v-model="form[`siteCode${n}`]" type="number" min="0"
                                                max="16777214" />
                                        </b-form-group>
                                    </b-col>

                                    <!-- Num User Code Digits -->
                                    <b-col cols="6">
                                    <validation-provider #default="{ errors }" name="Number of User Code Digits" rules="required|min_value:4|max_value:12">
                                        <b-form-group label="Number of User Code Digits" label-for="numberOfUserCodeDigits">
                                        <b-form-input id="numberOfUserCodeDigits" v-model="form.numberOfUserCodeDigits"
                                            type="number" min="4" max="12"
                                            :state="errors.length > 0 ? false : null" />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                        </b-form-group>
                                    </validation-provider>
                                    </b-col>
                                </b-row>

                                <!-- Action Buttons -->
                                <div class="d-flex justify-content-end mt-1">
                                    <b-button v-if="isEditing" variant="outline-danger" size="sm" class="mr-1"
                                        :disabled="isSaving || isDeleting" @click="deleteCardFormat">
                                        <b-spinner v-if="isDeleting" small class="mr-25" />
                                        <feather-icon v-else icon="TrashIcon" size="14" class="mr-25" />
                                        {{ $t('actions.delete') }}
                                    </b-button>

                                    <b-button variant="primary" size="sm" :disabled="isSaving || isDeleting"
                                        @click="saveCardFormat">
                                        <b-spinner v-if="isSaving" small class="mr-25" />
                                        <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                                        {{ isSaving ? $t('actions.saving') : $t('actions.save') }}
                                    </b-button>
                                </div>
                            </b-card-body>
                        </b-card>

                        <!-- Empty state -->
                        <div v-else class="d-flex align-items-center justify-content-center h-100 text-muted">
                            <span>{{ $t('dmp.deviceSetup.selectOrAddCardFormat') }}</span>
                        </div>
                    </b-col>

                </b-row>
            </b-form>
        </validation-observer>
    </b-modal>
</template>

<script>
import { ref, computed, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Swal from "sweetalert2";
import { useTemplateMode } from "../useTemplateMode";

export default {
    name: "CardFormatModal",
    components: {
        ValidationObserver,
        ValidationProvider,
    },
    model: {
        prop: "isActive",
        event: "update:is-active",
    },
    props: {
        isActive: {
            type: Boolean,
            required: true,
        },
        uuid: {
            type: String,
            required: true,
        },
        panelId: {
            type: [Number, String],
            required: true,
        },
        cardFormatsData: {
            type: Array,
            default: () => [],
        },
        source: {
            type: String,
            default: "default",
        },
    },
    setup(props, { emit }) {

        const vm = getCurrentInstance();
        const proxy = vm.root.proxy;
        const toast = useToast();
        const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);
        const formObserver = ref(null);

        const cardFormats = ref([]);
        const selectedFormat = ref(null);
        const isEditing = ref(false);
        const isSaving = ref(false);
        const isDeleting = ref(false);

        const tableFields = computed(() => [
            { key: "formatNumber", label: "#", sortable: true, thStyle: { width: "60px" } },
            { key: "formatName", label: "Name", sortable: true },
        ]);

        const validCardFormats = computed(() =>
            cardFormats.value.filter((f) => {
                const num = parseInt(f.formatNumber, 10);
                return !isNaN(num) && num >= 1 && num <= 8;
            })
        );

        const defaultForm = () => ({
            formatNumber: "",
            wiegandCodeLength: "26",
            siteCodePosition: "1",
            siteCodeLength: "8",
            userCodePosition: "9",
            userCodeLength: "16",
            requireSiteCode: false,
            numberOfUserCodeDigits: "5",
            formatName: "",
            siteCode1: "127",
            siteCode2: "",
            siteCode3: "",
            siteCode4: "",
            siteCode5: "",
            siteCode6: "",
            siteCode7: "",
            siteCode8: "",
        });

        const form = ref(defaultForm());

        const loadCardFormats = () => {
            cardFormats.value = Array.isArray(props.cardFormatsData)
                ? props.cardFormatsData
                : [];
        };

        const addNewCardFormat = () => {
            const usedNumbers = new Set(
                validCardFormats.value.map((f) => parseInt(f.formatNumber, 10))
            );
            const nextNumber = [1, 2, 3, 4, 5, 6, 7, 8].find((n) => !usedNumbers.has(n));

            if (!nextNumber) {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: proxy.$t("warning"),
                        text: "Maximum of 8 card formats reached",
                        icon: "AlertTriangleIcon",
                        variant: "warning",
                    },
                });
                return;
            }

            isEditing.value = false;
            selectedFormat.value = { formatNumber: nextNumber, formatName: "" };
            form.value = { ...defaultForm(), formatNumber: String(nextNumber) };

            if (formObserver.value) formObserver.value.reset();
        };

        const onRowSelected = (items) => {
            if (items.length === 0) {
                selectedFormat.value = null;
                isEditing.value = false;
                return;
            }

            const selected = items[0];
            isEditing.value = true;
            selectedFormat.value = selected;

            form.value = {
                formatNumber: String(parseInt(selected.formatNumber, 10)),
                wiegandCodeLength: String(parseInt(selected.wiegandCodeLength, 10) || 26),
                siteCodePosition: String(parseInt(selected.siteCodePosition, 10) || 1),
                siteCodeLength: String(parseInt(selected.siteCodeLength, 10) || 8),
                userCodePosition: String(parseInt(selected.userCodePosition, 10) || 9),
                userCodeLength: String(parseInt(selected.userCodeLength, 10) || 16),
                requireSiteCode: selected.requireSiteCode === "Y",
                numberOfUserCodeDigits: String(parseInt(selected.numberOfUserCodeDigits, 10) || 5),
                formatName: selected.formatName?.trim() || "",
                siteCode1: selected.siteCode1?.trim() || "",
                siteCode2: selected.siteCode2?.trim() || "",
                siteCode3: selected.siteCode3?.trim() || "",
                siteCode4: selected.siteCode4?.trim() || "",
                siteCode5: selected.siteCode5?.trim() || "",
                siteCode6: selected.siteCode6?.trim() || "",
                siteCode7: selected.siteCode7?.trim() || "",
                siteCode8: selected.siteCode8?.trim() || "",
            };
        };

        const saveCardFormat = async () => {
            const isValid = await formObserver.value.validate();
            if (!isValid) return;

            if (isTemplateMode.value) {
                const item = {
                    ...form.value,
                    requireSiteCode: form.value.requireSiteCode ? "Y" : "N",
                };
                const list = [...(props.cardFormatsData || [])];
                const idx = list.findIndex(
                    (f) =>
                        parseInt(f.formatNumber, 10) ===
                        parseInt(item.formatNumber, 10)
                );
                const merged =
                    idx !== -1 ? { ...list[idx], ...item } : item;
                if (idx !== -1) list.splice(idx, 1, merged);
                else list.push(merged);

                emitTemplateUpdate("card_formats", list);
                toast({
                    component: ToastificationContent,
                    props: {
                        title: proxy.$t("dmp.common.success"),
                        icon: "CheckIcon",
                        variant: "success",
                    },
                });
                selectedFormat.value = null;
                isEditing.value = false;
                emit("update:is-active", false);
                return;
            }

            isSaving.value = true;
            store.commit("dmp_panels/setOperationActive", true);

            try {
                const payload = {
                    ...form.value,
                    requireSiteCode: form.value.requireSiteCode ? "Y" : "N",
                };

                const response = await store.dispatch("dmp_panels/setCardFormat", {
                    uuid: props.uuid,
                    cardFormatData: payload,
                });

                if (response.success) {
                    await store.dispatch("dmp_panels/updatePanelData", {
                        id: props.panelId,
                        configSection: "card_formats",
                    });

                    emit("refetch-data");

                    toast({
                        component: ToastificationContent,
                        props: {
                            title: proxy.$t("dmp.common.success"),
                            icon: "CheckIcon",
                            variant: "success",
                        },
                    });

                    selectedFormat.value = null;
                    isEditing.value = false;
                    emit("update:is-active", false);

                } else {
                    throw new Error(response.message);
                }
            } catch (error) {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: proxy.$t("dmp.common.error"),
                        text: error.response?.data?.message || error.message,
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });
            } finally {
                isSaving.value = false;
                store.commit("dmp_panels/setOperationActive", false);
            }
        };

        const deleteCardFormat = async () => {
            const result = await Swal.fire({
                title: proxy.$t("messages.delete.title"),
                text: proxy.$t("messages.delete.body"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: proxy.$t("actions.delete"),
                cancelButtonText: proxy.$t("actions.cancel"),
                customClass: {
                    confirmButton: "btn btn-primary text-capitalize",
                    cancelButton: "btn btn-outline-danger ml-1",
                },
                buttonsStyling: false,
            });

            if (!result.isConfirmed) return;

            if (isTemplateMode.value) {
                const list = (props.cardFormatsData || []).filter(
                    (f) =>
                        parseInt(f.formatNumber, 10) !==
                        parseInt(form.value.formatNumber, 10)
                );
                emitTemplateUpdate("card_formats", list);
                toast({
                    component: ToastificationContent,
                    props: {
                        title: proxy.$t("dmp.common.success"),
                        icon: "CheckIcon",
                        variant: "success",
                    },
                });
                selectedFormat.value = null;
                isEditing.value = false;
                emit("update:is-active", false);
                return;
            }

            isDeleting.value = true;
            store.commit("dmp_panels/setOperationActive", true);

            try {
                const response = await store.dispatch("dmp_panels/deleteCardFormat", {
                    uuid: props.uuid,
                    formatNumber: form.value.formatNumber,
                });

                if (response.success) {
                    await store.dispatch("dmp_panels/updatePanelData", {
                        id: props.panelId,
                        configSection: "card_formats",
                    });

                    emit("refetch-data");

                    toast({
                        component: ToastificationContent,
                        props: {
                            title: proxy.$t("dmp.common.success"),
                            icon: "CheckIcon",
                            variant: "success",
                        },
                    });

                    selectedFormat.value = null;
                    isEditing.value = false;
                    emit("update:is-active", false);
                } else {
                    throw new Error(response.message);
                }
            } catch (error) {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: proxy.$t("dmp.common.error"),
                        text: error.response?.data?.message || error.message,
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });
            } finally {
                isDeleting.value = false;
                store.commit("dmp_panels/setOperationActive", false);
            }
        };

        watch(
            () => props.cardFormatsData,
            () => loadCardFormats(),
            { immediate: true, deep: true }
        );

        watch(
            () => props.isActive,
            (val) => {
                if (val) {
                    loadCardFormats();
                    selectedFormat.value = null;
                    isEditing.value = false;
                    form.value = defaultForm();
                }
            }
        );

        return {
            cardFormats,
            validCardFormats,
            selectedFormat,
            isEditing,
            isSaving,
            isDeleting,
            tableFields,
            form,
            formObserver,
            addNewCardFormat,
            onRowSelected,
            saveCardFormat,
            deleteCardFormat,
        };
    },
};
</script>

<style lang="scss">
.card-format-modal {
    .modal-dialog {
        max-width: 90vw;
    }
}
</style>